import { Injectable } from '@angular/core';
import { AwsBedrockService } from './aws-bedrock.service';

export interface GenerateStepsRequest {
  scenario: string;
  elementMap: any[];
  textElements: string[];
  url?: string;
}

export interface GenerateStepsResponse {
  success: boolean;
  steps: string[];
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiStepGeneratorService {
  constructor(private bedrockService: AwsBedrockService) { }

  /**
   * Generate test steps from scenario using AI
   */
  async generateSteps(request: GenerateStepsRequest): Promise<GenerateStepsResponse> {
    try {
      // Build the prompt with context
      const prompt = this.buildPrompt(request);

      // Call AI API
      const response = await this.callAI(prompt);

      // Parse the response into steps
      const steps = this.parseSteps(response);

      return {
        success: true,
        steps: steps
      };
    } catch (error: any) {
      return {
        success: false,
        steps: [],
        error: error.message || 'Failed to generate steps'
      };
    }
  }

  /**
   * Build prompt with scenario and page context
   */
  private buildPrompt(request: GenerateStepsRequest): string {
    const { scenario, elementMap, textElements, url } = request;

    // Extract element information
    const buttons = elementMap.filter(e => e.type === 'button').map(e => e.identifiers[0]).slice(0, 20);
    const inputs = elementMap.filter(e => e.type === 'input' || e.type === 'textbox').map(e => e.identifiers[0]).slice(0, 20);
    const links = elementMap.filter(e => e.type === 'link' || e.type === 'a').map(e => e.identifiers[0]).slice(0, 20);
    const dropdowns = elementMap.filter(e => e.type === 'dropdown' || e.type === 'select').map(e => e.identifiers[0]).slice(0, 20);
    const verifiableTexts = textElements.slice(0, 15);

    const prompt = `You are a test automation expert. Your job is to generate ACTIONABLE test steps that ACCOMPLISH the user's goal using the ACTUAL elements on the current page.

USER SCENARIO:
${scenario}

CURRENT PAGE URL:
${url || 'Not provided'}

⚠️ CRITICAL RULES - MUST FOLLOW:

1. THE USER'S SCENARIO IS YOUR ONLY GOAL: 
   - If user says "login", you MUST generate steps to enter credentials and click login
   - If user says "search", you MUST generate steps to enter search term and click search
   - If user says "fill form", you MUST generate steps to enter data in fields

2. USE THE ACTUAL PAGE ELEMENTS BELOW:
   - Match input field names to the user's intent (e.g., "username" or "email" field for login)
   - Match button names to the user's action (e.g., "Sign In" or "Login" button)

3. NEVER GENERATE THESE UNLESS SPECIFICALLY REQUESTED:
   - ❌ "Navigate to" steps (user is already on the page)
   - ❌ "Verify page contains" steps (unless user asks to verify)
   - ❌ Opening pages or clicking links (unless user asks)

4. ALWAYS GENERATE THESE FOR THE USER'S GOAL:
   - ✅ "Enter {{value}} in [field] textbox" for data entry
   - ✅ "Click [button] button" for actions
   - ✅ "Wait for 2 seconds" after actions that change the page

AVAILABLE PAGE ELEMENTS ON CURRENT PAGE:

BUTTONS (${buttons.length}):
${buttons.length > 0 ? buttons.map(b => `- ${b}`).join('\n') : '- (No buttons found on current page)'}

INPUT FIELDS (${inputs.length}):
${inputs.length > 0 ? inputs.map(i => `- ${i}`).join('\n') : '- (No input fields found on current page)'}

LINKS (${links.length}):
${links.length > 0 ? links.map(l => `- ${l}`).join('\n') : '- (No links found on current page)'}

DROPDOWNS (${dropdowns.length}):
${dropdowns.length > 0 ? dropdowns.map(d => `- ${d}`).join('\n') : '- (No dropdowns found on current page)'}

VERIFIABLE TEXT ON PAGE (${verifiableTexts.length}):
${verifiableTexts.length > 0 ? verifiableTexts.map(t => `- ${t}`).join('\n') : '- (No text elements captured)'}

STEP FORMAT - USE EXACTLY THIS FORMAT:
   - Data Entry: "Enter {{value}} in [exact identifier from INPUT FIELDS] textbox"
   - Button Click: "Click [exact identifier from BUTTONS] button"
   - Link Click: "Click [exact identifier from LINKS] link"
   - Dropdown: "Select {{option}} from [exact identifier from DROPDOWNS] dropdown"
   - Wait: "Wait for 2 seconds" (add after clicks/enters)
   - Verify Presence: "Verify page contains [exact text from VERIFIABLE TEXT]" (only if user asks)
   - Verify Absence: "Verify [element name] does not exist" (when checking element should NOT be present)
   - Verify Page Absence: "Verify page does not contain [text]" (when checking text should NOT appear)
   - Table Negative: "Verify table does not contain [text]" (when checking table should NOT have specific data)
   - Column Negative: "Verify column [name] does not contain [value]" (when checking column should NOT have value)
   - Table Row Validation with Operators: "Verify table columns: [Column1]=[Value1], [Column2] [operator] [Value2]"
     Supported operators: equals (=), contains, not equals, not contains, greater than, less than, starts with, ends with, is empty, is not empty
     Examples:
     • "Verify table columns: Name=John, Role not equals Admin" (find row where Name is John AND Role is NOT Admin)
     • "Verify table columns: Status contains Active, Balance greater than 1000" (Status contains "Active" AND Balance > 1000)
     • "Verify table columns: Email is not empty, Description is empty" (Email has content AND Description is empty)
   - Key Press: "Press Enter Key" or "Press Tab Key"

📋 EXAMPLE FOR "LOGIN WITH USERNAME AND PASSWORD":

CORRECT OUTPUT (THIS IS WHAT YOU MUST GENERATE):
Enter {{username}} in username textbox
Enter {{password}} in password textbox
Click Sign In button
Wait for 2 seconds

WRONG OUTPUT (NEVER DO THIS):
Navigate to https://example.com/login
Verify page contains Sign In to Your Account
Wait for 3 seconds
Verify page contains Forgot Your Password?

⚠️ ANALYSIS OF WRONG OUTPUT:
- ❌ "Navigate to" - User is already on the page!
- ❌ "Verify page contains" - User didn't ask to verify anything
- ❌ No "Enter {{username}}" - This is the main action!
- ❌ No "Enter {{password}}" - This is the main action!
- ❌ No "Click Sign In" - This is how login happens!

📋 EXAMPLE FOR "CHECK ERROR MESSAGE DOES NOT APPEAR":

CORRECT OUTPUT FOR NEGATIVE VALIDATION:
Enter {{validEmail}} in email textbox
Enter {{validPassword}} in password textbox
Click Login button
Wait for 2 seconds
Verify error message does not exist
Verify page does not contain Invalid credentials

USAGE OF NOT-EXIST VALIDATIONS:
- Use "does not exist" for elements that should be ABSENT (error messages, warnings, locked icons)
- Use "page does not contain" for text that should NOT appear anywhere
- Use "table does not contain" for data that should NOT be in tables
- Use "column [name] does not contain [value]" for specific column validations

🎯 YOUR TASK: Generate ONLY the steps needed to accomplish "${scenario}"

Return ONLY step commands, one per line. NO explanations. NO navigation. NO verification unless requested.

GENERATE STEPS NOW:`;

    return prompt;
  }

  /**
   * Call AWS Bedrock AI (OpenAI or Claude) directly
   */
  private async callAI(prompt: string): Promise<string> {
    try {
      // Call AWS Bedrock service directly - no HTTP overhead
      const content = await this.bedrockService.generateTestSteps(prompt);
      return content.trim();
    } catch (error: any) {
      console.error('[AiStepGenerator] AWS Bedrock error:', error);
      
      // Provide user-friendly error messages
      if (error.message?.includes('credentials')) {
        throw new Error('AWS credentials not configured. Please check your AWS Bedrock setup.');
      } else if (error.message?.includes('region')) {
        throw new Error('AWS region not configured. Please check your AWS Bedrock configuration.');
      } else if (error.message?.includes('throttl')) {
        throw new Error('AWS Bedrock rate limit exceeded. Please try again later.');
      } else if (error.message?.includes('quota')) {
        throw new Error('AWS Bedrock quota exceeded. Please check your AWS account.');
      } else {
        throw new Error(`AI generation failed: ${error.message || 'Unknown error'}`);
      }
    }
  }

  /**
   * Parse AI response into individual steps
   */
  private parseSteps(response: string): string[] {
    if (!response) {
      return [];
    }

    // Split by lines and clean up
    const lines = response.split('\n')
      .map(line => line.trim())
      .filter(line => {
        // Remove empty lines
        if (!line) return false;
        
        // Remove lines that are just numbers or bullets
        if (/^\d+\.?\s*$/.test(line)) return false;
        if (/^[-*•]\s*$/.test(line)) return false;
        
        // Remove markdown headings
        if (line.startsWith('#')) return false;
        
        // Remove apology or explanation messages - AGGRESSIVE FILTERING
        const lowerLine = line.toLowerCase();
        
        // Filter out AI explanation phrases
        const explanationPhrases = [
          'apologize', 'sorry', 'i cannot', 'i apologize', 'i would need',
          'to provide', 'here are', 'steps:', 'scenario', 'correct output',
          'wrong output', 'analysis', 'the page description', 'does not include',
          'information about', 'need information', 'presence and identifier',
          'would need', 'based on', 'without', 'currently', 'available',
          'the current page', 'given the', 'please provide', 'more details'
        ];
        
        if (explanationPhrases.some(phrase => lowerLine.includes(phrase))) {
          return false;
        }
        
        // ONLY keep lines that look like actual test commands
        // Must start with action verbs or be a Wait command
        const isActionCommand = 
          lowerLine.startsWith('enter') ||
          lowerLine.startsWith('click') ||
          lowerLine.startsWith('select') ||
          lowerLine.startsWith('press') ||
          lowerLine.startsWith('wait') ||
          lowerLine.startsWith('verify') ||
          lowerLine.startsWith('navigate');
        
        if (!isActionCommand) {
          return false;
        }
        
        return true;
      })
      .map(line => {
        // Remove leading numbers, bullets, or markers
        return line.replace(/^\d+[\.)]\s*/, '')
                   .replace(/^[-*•]\s*/, '')
                   .trim();
      })
      .filter(line => line.length > 5); // Filter out very short lines

    // Log what was filtered
    if (lines.length === 0) {}

    // Validate steps quality
    const hasActionableSteps = lines.some(line => {
      const lowerLine = line.toLowerCase();
      return lowerLine.includes('enter') || 
             lowerLine.includes('click') || 
             lowerLine.includes('select') ||
             lowerLine.includes('press');
    });

    if (!hasActionableSteps && lines.length > 0) {
      console.warn('⚠️ AI generated only verification/wait steps, no actionable steps found!');
      console.warn('Steps received:', lines);
      console.warn('This usually means the page does not have the elements needed for the scenario.');
    }

    return lines;
  }
}
